import { _ } from "../../../core/polyglot/polyglot.js";
import { guid } from "../../../core/helpers/string-util.js";
import Model from "../../Model.js";

/**
 * `KeyFigurePreset` is a group of keyFigures, defined by the agency or by the user.
 *
 * @param {{}} spec - Parameter object
 * @param {string} spec.id - Unique identifier of the preset
 * @param {string} spec.name - Name of the preset (the name doesn't need to be translated)
 * @param {string} spec.description - The description of the purpose of the preset
 * @param {keyFigure[]} spec.keyFigures - List of keyFigures contained in the preset
 * @param {boolean} spec.isSystemPreset - Flag use to know if a preset was defined in agency
 * @param {boolean} spec.isVisible - Flag used to know if a preset can be seen
 * @param {*} my - protected fields holder
 * @return {*} keyFigurePreset
 */
export default class KeyFigurePreset extends Model {
	constructor(spec = {}) {
		super(spec);
		this._id = spec.id || guid();
		this._name = spec.name;
		this._description = spec.description || "";
		this._keyFigures = spec.keyFigures || [];
		this._isSystemPreset = spec.isSystemPreset;
		this._isVisible = spec.isVisible;
	}

	getId() {
		return this._id;
	}

	getName() {
		return this._name;
	}

	setName(newName) {
		this._name = newName;
	}

	getDescription() {
		return this._description;
	}

	setDescription(newDescription) {
		this._description = newDescription;
	}

	getKeyFigureById(id) {
		return this._keyFigures.find((keyFigure) => keyFigure.getId() === id);
	}

	getKeyFigures() {
		return this._keyFigures;
	}

	setKeyFigures(newKeyFigures) {
		this._keyFigures = newKeyFigures;
	}

	isCustom() {
		return false;
	}

	isSystemPreset() {
		return this._isSystemPreset;
	}

	isVisible() {
		return this._isVisible;
	}

	setVisibility(visible) {
		this._isVisible = visible;
	}

	isVisibleKeyFigures() {
		return false;
	}

	containsKeyFiguresFrom(otherPreset) {
		return this.getKeyFigures().some((keyFigure) =>
			otherPreset.containsKeyFigure(keyFigure),
		);
	}

	containsKeyFigure(aKeyFigure) {
		return this._keyFigures.indexOf(aKeyFigure) !== -1;
	}

	addKeyFigure(keyFigure) {
		if (this.containsKeyFigure(keyFigure)) {
			return;
		}

		this._keyFigures = [keyFigure, ...this._keyFigures];
	}

	removeKeyFigure(keyFigure) {
		this._keyFigures = this._keyFigures.filter(
			(kpi) => kpi.getId() !== keyFigure.getId(),
		);
	}

	_serializedProperties() {
		return {
			shortName: this.getId(),
			name: this.getName().toString(),
			description: this.getDescription().toString(),
			isVisible: this.isVisible(),
			isSystemPreset: this.isSystemPreset(),
			keyFigureIds: this.getKeyFigures().map((kpi) => kpi.getId()),
		};
	}

	static materialize(data, context) {
		data = Object.create(data);
		data.id = data.shortName;
		data.name = this._mostSuitableName(data.id, data.name);

		data.keyFigures = data.keyFigureIds
			.map((keyFigureId) => context.keyFigures[keyFigureId])
			.filter(
				(keyFigure) =>
					// Temporary solution: silently ignore if not in cache(FTGP-4913 should remove this)!
					!!keyFigure,
			);

		return this.buildFrom(data);
	}

	// The server always associates a name to every key figure preset,
	// even the system ones. This name is not what the POs want and is
	// not translated. The quick fix below solves both problems at the
	// same time. A better solution would be for the server to only
	// send a name when the name has been customized in some way. This
	// is much harder to implement and check for correctness though.
	static _mostSuitableName(id, name) {
		if (id === "Overview") {
			return _("Overview page KPIs");
		} else if (id === "MyKeyFigures") {
			return _("Period report KPIs");
		}
		return name;
	}
}
